import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, PaginationQueryWithFields, Version, Versions } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Supplier, SupplierVersion, UpsertSupplier } from "./types";

export const useSuppliersApi = () => {
  const api = useApi();

  const getAdminSupplier = useCallback(
    (supplierId: string, signal: AbortSignal) => {
      const url = `/admin/suppliers/${supplierId}`;
      return useSWRMutation<Supplier | null, CustomError, string>(url, (url) => api.get<Supplier>({ url, signal }));
    },
    [api],
  );

  const useAdminSuppliers = useCallback(
    <K extends keyof Supplier = keyof Supplier>(
      query: PaginationQueryWithFields<Pick<Supplier, K>>,
      signal: AbortSignal,
      hasPermission?: boolean,
    ) => {
      const url = buildUrl<Pick<Supplier, K>>("/admin/suppliers", query);

      const key = hasPermission === false ? false : url;

      return useSWR<Paginated<Pick<Supplier, K>> | null, CustomError, string | null>(
        key as string | null,
        (url: string) => api.get<Paginated<Pick<Supplier, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminSupplier = useCallback(
    async (body: UpsertSupplier): Promise<{ id: string }> => {
      const url = `/admin/suppliers`;
      const id = await api.post<UpsertSupplier, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminSupplier = useCallback(
    async (supplierId: string, body: UpsertSupplier): Promise<void> => {
      const url = `/admin/suppliers/${supplierId}`;
      (await api.put<UpsertSupplier>(url, body),
        api.mutateCache(["/admin/suppliers", `/admin/suppliers/${supplierId}/versions`]));
    },
    [api],
  );

  const deleteAdminSupplier = useCallback(
    async (supplierId: string): Promise<void> => {
      const url = `/admin/suppliers/${supplierId}`;
      await api.del(url).then(() => {
        api.mutateCache(["/admin/suppliers"]);
      });
    },
    [api],
  );

  const getAdminSupplierHistory = useCallback(
    (query: PaginationQuery, SupplierID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/suppliers/${SupplierID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminSupplierVersion = useCallback(
    (SupplierId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/suppliers/${SupplierId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<SupplierVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<SupplierVersion>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    getAdminSupplier,
    useAdminSuppliers,
    insertAdminSupplier,
    updateAdminSupplier,
    deleteAdminSupplier,
    getAdminSupplierHistory,
    getAdminSupplierVersion,
  };
};
